import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SupplyOrders, SupplyItems } from '../types';
import { RootState } from '../store';
import API from '../lib/API';
import { setSupplyOrders, setSupplyItems } from '../slices/boutiquesSlice';

import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Alert from 'react-bootstrap/Alert';
import Table from 'react-bootstrap/Table';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import ItemAutocomplete from './ItemAutocomplete';

interface SupplyOrderFormModalProps {
    show: boolean;
    handleClose: () => void;
    token: string;
    boutiqueId: number;
    order?: SupplyOrders;
}

const SupplyOrderFormModal: React.FC<SupplyOrderFormModalProps> = ({ 
    show, 
    handleClose, 
    token, 
    boutiqueId, 
    order 
}) => {
    const dispatch = useDispatch();
    const boutiqueItems = useSelector((state: RootState) => state.boutiques.boutiqueItems);
    const supplyItems = useSelector((state: RootState) => state.boutiques.supplyItems);
    const [status, setStatus] = useState('pending');
    const [exchangeRate, setExchangeRate] = useState('');
    const [orderCurrency, setOrderCurrency] = useState('');
    const [saleCurrency, setSaleCurrency] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [items, setItems] = useState<SupplyItems[]>([]);
    const [newItem, setNewItem] = useState({
        boutiqueItemId: '',
        quantity: 1,
        orderCurrencyPrice: 0
    });

    useEffect(() => {
        if (order) {
            setStatus(order.status);
            setExchangeRate(order.exchangeRate.toString());
            setOrderCurrency(order.orderCurrency);
            setSaleCurrency(order.saleCurrency);
            // Get items from Redux store
            setItems(supplyItems.filter(item => item.supplyOrderId === order.supplyOrderId));
        } else {
            setStatus('pending');
            setExchangeRate('');
            setOrderCurrency('USD');
            setSaleCurrency('USD');
            setItems([]);
        }
    }, [order, supplyItems]);

    const handleAddItem = async () => {
        if (!newItem.boutiqueItemId || newItem.quantity <= 0 || newItem.orderCurrencyPrice <= 0) {
            setError('Please fill in all required fields for the item');
            return;
        }

        const boutiqueItem = boutiqueItems.find(item => item.boutiqueItemId === Number(newItem.boutiqueItemId));
        if (!boutiqueItem) {
            setError('Selected item not found');
            return;
        }

        if (!order?.supplyOrderId) {
            setError('Please save the order first before adding items');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const itemData = {
                boutiqueId,
                supplyOrderId: order.supplyOrderId,
                boutiqueItemId: Number(newItem.boutiqueItemId),
                quantity: newItem.quantity,
                orderCurrencyPrice: newItem.orderCurrencyPrice
            };

            await API.createSupplyItem(token, itemData);
            const updatedItems = await API.getSupplyItems(token, boutiqueId);
            dispatch(setSupplyItems(updatedItems));
            setItems(updatedItems.filter(item => item.supplyOrderId === order.supplyOrderId));

            setNewItem({
                boutiqueItemId: '',
                quantity: 1,
                orderCurrencyPrice: 0
            });
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to add item');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const exchangeRateNum = parseFloat(exchangeRate);
            if (isNaN(exchangeRateNum)) {
                setError('Please enter a valid exchange rate');
                return;
            }

            const data = {
                boutiqueId,
                status,
                exchangeRate: exchangeRateNum,
                orderCurrency,
                saleCurrency
            };

            if (order) {
                await API.updateSupplyOrder(token, order.supplyOrderId, data);
            } else {
                await API.createSupplyOrder(token, data);
            }

            const updatedOrders = await API.getSupplyOrders(token, boutiqueId);
            dispatch(setSupplyOrders(updatedOrders));
            handleClose();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!order) return;

        setLoading(true);
        setError('');

        try {
            // Delete all items first
            for (const item of items) {
                await API.deleteSupplyItem(token, item.supplyItemId);
            }
            
            await API.deleteSupplyOrder(token, order.supplyOrderId);
            const updatedOrders = await API.getSupplyOrders(token, boutiqueId);
            const updatedItems = await API.getSupplyItems(token, boutiqueId);
            dispatch(setSupplyOrders(updatedOrders));
            dispatch(setSupplyItems(updatedItems));
            handleClose();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveItem = async (itemId: number) => {
        if (!order) return;

        setLoading(true);
        setError('');

        try {
            await API.deleteSupplyItem(token, itemId);
            const updatedItems = await API.getSupplyItems(token, boutiqueId);
            dispatch(setSupplyItems(updatedItems));
            setItems(updatedItems.filter(item => item.supplyOrderId === order.supplyOrderId));
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to remove item');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal show={show} onHide={handleClose} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>{order ? 'Edit Supply Order' : 'New Supply Order'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && <Alert variant="danger">{error}</Alert>}
                <Form onSubmit={handleSubmit}>
                    <h5>Order Details</h5>
                    <Form.Group className="mb-3">
                        <Form.Label>Status</Form.Label>
                        <Form.Select
                            value={status}
                            onChange={(e) => setStatus(e.target.value)}
                            required
                        >
                            <option value="PENDING">Pending</option>
                            <option value="PAID">Paid</option>
                        </Form.Select>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Exchange Rate</Form.Label>
                        <Form.Control
                            type="number"
                            min="0"
                            step="0.0001"
                            value={exchangeRate}
                            onChange={(e) => setExchangeRate(e.target.value)}
                            required
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Order Currency</Form.Label>
                        <Form.Control
                            type="text"
                            maxLength={3}
                            value={orderCurrency}
                            onChange={(e) => setOrderCurrency(e.target.value.toUpperCase())}
                            required
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Sale Currency</Form.Label>
                        <Form.Control
                            type="text"
                            maxLength={3}
                            value={saleCurrency}
                            onChange={(e) => setSaleCurrency(e.target.value.toUpperCase())}
                            required
                        />
                    </Form.Group>

                    {order && (
                        <>
                            <hr className="my-4" />
                            <h5>Add Item</h5>
                            <Row className="mb-3">
                                <Col md={5}>
                                    <Form.Group className="mb-2">
                                        <Form.Label>Boutique Item</Form.Label>
                                        <ItemAutocomplete
                                            items={boutiqueItems}
                                            value={newItem.boutiqueItemId}
                                            onChange={(itemId) => {
                                                setNewItem({ ...newItem, boutiqueItemId: itemId });
                                            }}
                                            required
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-2">
                                        <Form.Label>Quantity</Form.Label>
                                        <Form.Control
                                            type="number"
                                            min="1"
                                            value={newItem.quantity}
                                            onChange={(e) => setNewItem({ ...newItem, quantity: parseInt(e.target.value) })}
                                            required
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group className="mb-2">
                                        <Form.Label>Order Price</Form.Label>
                                        <div className="d-flex">
                                            <Form.Control
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                value={newItem.orderCurrencyPrice}
                                                onChange={(e) => setNewItem({ ...newItem, orderCurrencyPrice: parseFloat(e.target.value) })}
                                                required
                                            />
                                            <Button 
                                                variant="secondary" 
                                                onClick={handleAddItem} 
                                                className="ms-2"
                                                disabled={loading}
                                            >
                                                Add
                                            </Button>
                                        </div>
                                    </Form.Group>
                                </Col>
                            </Row>

                            <h5 className="mt-4">Items</h5>
                            <Table striped bordered hover>
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Quantity</th>
                                        <th>Order Price</th>
                                        <th>Sale Price</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((item) => {
                                        const boutiqueItem = boutiqueItems.find(bi => bi.boutiqueItemId === item.boutiqueItemId);
                                        return (
                                            <tr key={item.supplyItemId}>
                                                <td>{boutiqueItem?.name || 'Unknown Item'}</td>
                                                <td>{item.quantity}</td>
                                                <td>{Number(item.orderCurrencyPrice).toFixed(2)} {orderCurrency}</td>
                                                <td>{item.saleCurrencyPrice ? `${Number(item.saleCurrencyPrice).toFixed(2)} ${saleCurrency}` : '-'}</td>
                                                <td>
                                                    <Button 
                                                        variant="danger" 
                                                        size="sm"
                                                        onClick={() => handleRemoveItem(item.supplyItemId)}
                                                        disabled={loading}
                                                    >
                                                        Remove
                                                    </Button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>
                        </>
                    )}
                </Form>
            </Modal.Body>
            <Modal.Footer>
                {order && (
                    <Button 
                        variant="danger" 
                        onClick={handleDelete}
                        disabled={loading}
                        className="me-auto"
                    >
                        Delete
                    </Button>
                )}
                <Button variant="secondary" onClick={handleClose} disabled={loading}>
                    Cancel
                </Button>
                <Button 
                    variant="primary" 
                    onClick={handleSubmit}
                    disabled={loading}
                >
                    {loading ? 'Saving...' : 'Save'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default SupplyOrderFormModal; 
